import { useState, useEffect } from "react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useCompany, useUpdateCompany } from "@/hooks/useCompanies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PrivacySettingsTab } from "@/components/shared/PrivacySettingsTab";

export default function CompanySettings() {
  const { data: currentUser } = useCurrentUser();
  const companyId = currentUser?.company_id ? String(currentUser.company_id) : undefined;
  const { data: company, isLoading } = useCompany(companyId);
  const updateCompany = useUpdateCompany(currentUser?.company_id);
  const { toast } = useToast();

  const [name, setName] = useState("");

  useEffect(() => {
    if (company?.name) setName(company.name);
  }, [company?.name]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    updateCompany.mutate(name.trim(), {
      onSuccess: () => {
        toast({ title: "Nome atualizado com sucesso." });
      },
      onError: () => {
        toast({ title: "Erro ao atualizar o nome.", variant: "destructive" });
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie as informações da sua empresa e preferências de privacidade.
        </p>
      </div>

      <Tabs defaultValue="empresa">
        <TabsList>
          <TabsTrigger value="empresa">Empresa</TabsTrigger>
          <TabsTrigger value="privacidade">Privacidade</TabsTrigger>
        </TabsList>

        <TabsContent value="empresa" className="mt-6">
          <div className="max-w-lg">
            <Card>
              <CardHeader>
                <CardTitle>Nome da empresa</CardTitle>
                <CardDescription>
                  Este nome aparece para indicadores e vendedores nos convites e campanhas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="company-name">Nome</Label>
                    <Input
                      id="company-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nome da empresa"
                    />
                  </div>
                  <Button type="submit" disabled={updateCompany.isPending || !name.trim()}>
                    {updateCompany.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Salvar
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="privacidade" className="mt-6">
          <div className="max-w-lg">
            <PrivacySettingsTab />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
